// /compliance/dpdp — a public readiness page: what India's phased DPDP framework asks institutions
// to prepare for and which shipped controls can support that programme. Modeled on SwitchPage
// (MarketingTopBar + SiteFooter + MarketingMotion chrome).
//
// EVERY claim here is grounded in the shipped DPDP machinery (CLAUDE.md "DPDP machinery",
// core/consent.py, core/retention.py, core/breach.py, core/dpdp.py, core/data_classification.py,
// invariants I-08/I-09/I-10) and stays inside the approved wording of Security.js + the claims
// registry (docs/marketing/CLAIMS_REGISTRY.md S5/S7/S8/DR1). Plain-language statements of the Act's
// obligations only — never legal advice, never a certification, never an invented number.
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { m, useReducedMotion } from "framer-motion";
import {
  ArrowRight, Check, CheckCircle2, ScrollText, Archive, Baby, Timer, Eraser, Lock, ShieldCheck,
} from "lucide-react";
import { MarketingTopBar, SiteFooter, ContactSalesModal } from "../components/marketing/shared";
import { MarketingMotion } from "../components/marketing/MarketingMotion";
import MarketingSeo from "../components/marketing/MarketingSeo";
import BrandWordmarkText from "../components/BrandWordmarkText";
import { Button } from "../components/ui/primitives";
import { init as initAnalytics, track } from "../lib/analytics";
import { CTA_PRIMARY_LABEL } from "../components/marketing/navigation";

const EASE = [0.22, 1, 0.36, 1];

// Five privacy duties → five supporting controls. `act` is intentionally readiness language because
// the Act and 2025 Rules have staged commencement; this page is product copy, not legal advice.
const OBLIGATIONS = [
  {
    id: "consent",
    icon: ScrollText,
    accent: "text-primary bg-primary/10",
    obligation: "Consent",
    act: "Where consent is the applicable ground, institutions need a clear notice, an affirmative decision, and a withdrawal path that is comparable to the way consent was given.",
    mechanism: "An append-only consent ledger",
    points: [
      ["Every decision is a new immutable entry.", "Grants, rejections, and withdrawals each land as a fresh ledger row — database permissions prevent the application from editing or deleting past records."],
      ["Withdrawal is symmetric.", "Withdrawing consent is just another entry, as easy as granting it — the current state is simply the latest decision on record."],
      ["The notice itself is versioned evidence.", "The exact consent notice a guardian accepted is versioned and snapshotted with a cryptographic digest, so historical consent evidence stays independently verifiable — even after the wording changes."],
    ],
  },
  {
    id: "retention",
    icon: Archive,
    accent: "text-teal-dark bg-teal/10",
    obligation: "Purpose & storage limitation",
    act: "Institutions need purpose-linked retention decisions and a governed way to erase eligible data while preserving records that another valid obligation requires them to keep.",
    mechanism: "Classification-driven retention with auto-purge",
    points: [
      ["Every collection of data is classified.", "Each type of record in the platform carries a classification — personal, sensitive, or child data — and a defined retention window."],
      ["Short-lived data purges itself.", "Operational data like notifications, messages, and consent links is automatically purged once its window passes."],
      ["Conservative by design.", "Financial, academic, audit, and consent records are excluded from unattended purges and move through a governed retention or erasure review."],
    ],
  },
  {
    id: "children",
    icon: Baby,
    accent: "text-sky-dark bg-sky/10",
    obligation: "Children's data",
    act: "When the relevant child-data provisions apply, an institution needs verifiable guardian consent and controls against detrimental processing, tracking, or targeted advertising.",
    mechanism: "Guardian-consent gates and reviewed data boundaries",
    points: [
      ["Guardian consent is captured at onboarding.", "The parent app opens with a guardian-consent gate, and the student app is limited to Class 5 and above — younger children are served through their parent's account."],
      ["The whole student record is treated as child data.", "Student records are classified as children's data by default, with the stricter handling that classification carries everywhere in the platform."],
      ["Student-AI health context has an explicit boundary.", "Current student-AI context excludes the named health, infirmary, wellbeing, and counselling sources. Every new integration still requires review."],
      ["AI identifier safeguards are configured and auditable.", "The default router pattern-redacts common identifiers; names are tokenized when the calling flow supplies known values. Permissions remain the access boundary."],
    ],
  },
  {
    id: "breach",
    icon: Timer,
    accent: "text-orange-dark bg-orange-soft",
    obligation: "Breach notification",
    act: "When the relevant breach rules apply, affected people and the Data Protection Board must be informed in the prescribed form and sequence; the detailed Board update has a 72-hour timeline unless extended.",
    mechanism: "An internal 72-hour evidence timer",
    points: [
      ["Reporting starts an internal timer.", "A recorded breach is stamped with an operational deadline 72 hours from discovery and tracked from open, through notified, to resolved."],
      ["The timer is evidence, not legal advice.", "Overdue cases surface on the compliance dashboard, while the school and its advisers determine the required recipients, timing, content, and any extension."],
      ["Follow-up can be routed.", "The breach event can create reviewed staff work or configured notification steps; provider acceptance and statutory completion remain separate evidence."],
    ],
  },
  {
    id: "erasure",
    icon: Eraser,
    accent: "text-positive bg-positive/10",
    obligation: "Erasure & data-principal rights",
    act: "When applicable rights are in force, families need a route to request access, correction, completion, updating, or erasure, subject to lawful retention and identity review.",
    mechanism: "Erasure that respects statutory retention",
    points: [
      ["Erasure redacts eligible fields in place.", "A student's name, contact details, and identifiers are redacted across supported records, while records held by configured retention rules remain restricted for review."],
      ["Export on request.", "A supported data-export package can be prepared for an authorised request and reviewed before release."],
      ["Corrections are a governed workflow.", "Students and parents file field-scoped correction requests that the school reviews and approves — the record changes through an audited path, not a phone call."],
    ],
  },
];

// The hero's product proof: a token-accurate, quietly-animated recreation of the append-only
// consent ledger. Data is SYNTHETIC and labeled — the append-only mechanics are the real product.
const LEDGER_ROWS = [
  { decision: "Granted", ok: true, purpose: "Registration data processing", who: "Guardian of Aarav S.", when: "12 Jun 2026" },
  { decision: "Granted", ok: true, purpose: "APAAR ID sharing", who: "Guardian of Meera P.", when: "28 Jun 2026" },
  { decision: "Withdrawn", ok: false, purpose: "APAAR ID sharing", who: "Guardian of Meera P.", when: "21 Jul 2026" },
];
const ledgerItem = {
  hidden: { opacity: 0, y: 14 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: EASE } },
};

function ConsentLedgerPreview({ reduce, testId = "dpdp-ledger-preview" }) {
  return (
    <figure className="relative" data-testid={testId}>
      <div className="absolute -inset-6 rounded-[2rem] bg-teal/10 blur-3xl pointer-events-none" aria-hidden="true" />
      <m.div
        role="img"
        aria-label="Preview of the append-only consent ledger: each consent grant and withdrawal is its own dated entry, and past entries cannot be edited or deleted. Sample data."
        className="relative rounded-2xl border border-line bg-surface shadow-elevated overflow-hidden"
        variants={{ hidden: {}, show: { transition: { staggerChildren: reduce ? 0 : 0.14, delayChildren: reduce ? 0 : 0.15 } } }}
        initial={reduce ? false : "hidden"}
        whileInView="show"
        viewport={{ once: true, amount: 0.4 }}
      >
        <div aria-hidden="true">
          <m.div variants={ledgerItem} className="flex items-center justify-between gap-3 border-b border-line px-4 py-3">
            <span className="inline-flex items-center gap-2 min-w-0">
              <span className="w-7 h-7 rounded-lg bg-teal/10 grid place-items-center shrink-0"><ScrollText className="w-4 h-4 text-teal-dark" /></span>
              <span className="font-heading font-semibold text-ink text-[13.5px] truncate">Consent ledger</span>
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-teal/10 border border-teal/30 px-2.5 py-1 text-[10.5px] font-bold text-teal-dark whitespace-nowrap">
              <Lock className="w-3 h-3" /> APPEND-ONLY
            </span>
          </m.div>
          <div className="px-4 py-3.5 space-y-2">
            {LEDGER_ROWS.map((r, i) => (
              <m.div key={i} variants={ledgerItem}
                className="flex items-center gap-3 rounded-xl border border-line bg-canvas px-3 py-2.5">
                <span className={`w-2 h-2 rounded-full shrink-0 ${r.ok ? "bg-positive" : "bg-orange"}`} />
                <span className="min-w-0">
                  <span className="block text-[12.5px] font-semibold text-ink truncate">{r.decision} — {r.purpose}</span>
                  <span className="block text-[11px] text-ink-subtle truncate">{r.who}</span>
                </span>
                <span className="ml-auto text-[11px] tabular-nums text-ink-subtle whitespace-nowrap">{r.when}</span>
              </m.div>
            ))}
          </div>
          <m.div variants={ledgerItem} className="flex items-start gap-2.5 border-t border-line bg-muted/40 px-4 py-3">
            <Lock className="w-3.5 h-3.5 text-ink-subtle mt-0.5 shrink-0" />
            <p className="text-[11.5px] text-ink-2 leading-relaxed">
              Past entries can't be edited or deleted — a withdrawal is a <span className="font-semibold text-ink">new entry</span>, never an overwrite.
            </p>
          </m.div>
        </div>
      </m.div>
      <figcaption className="mt-2.5 text-[11.5px] text-ink-subtle text-center">Illustrative preview — sample data. The append-only mechanics are the shipped product.</figcaption>
    </figure>
  );
}

export default function ComplianceDpdp() {
  const reduce = useReducedMotion();
  const navigate = useNavigate();
  const [salesOpen, setSalesOpen] = useState(false);

  useEffect(() => {
    initAnalytics();
    window.scrollTo(0, 0);
  }, []);

  const reveal = (delay = 0) => ({
    initial: reduce ? false : { opacity: 0, y: 24 },
    whileInView: reduce ? undefined : { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.25 },
    transition: { duration: 0.55, ease: EASE, delay: reduce ? 0 : delay },
  });

  return (
    <MarketingMotion>
    <div className="min-h-screen bg-canvas text-ink flex flex-col landing-page" data-testid="dpdp-page">
      <MarketingSeo
        title="DPDP Act 2023 for Indian Schools — Controls | Vidyapeeth360"
        description="Review privacy duties schools should prepare for and how Vidyapeeth360 consent, retention, access, audit and erasure workflows can support their programme."
        path="/compliance/dpdp"
      />
      <MarketingTopBar onBookDemo={() => setSalesOpen(true)} />
      <main className="flex-1">
        {/* Header — the DPDP promise, with the consent ledger as its proof. */}
        <section className="mx-auto max-w-6xl px-5 pt-12 sm:pt-16 pb-10 grid gap-12 lg:grid-cols-[minmax(0,1fr)_minmax(0,440px)] lg:items-center">
          <div className="max-w-3xl">
            <p className="inline-flex items-center gap-2 rounded-full border border-line bg-surface px-3.5 py-1.5 text-[13px] font-semibold text-ink-2">
              <ShieldCheck className="w-4 h-4 text-primary" aria-hidden="true" /> DPDP Act 2023 · For schools
            </p>
            <h1 className="mt-4 font-heading font-bold text-3xl sm:text-[40px] leading-[1.12] text-ink tracking-tight">
              Build a reviewable privacy programme around children's data.
            </h1>
            <p className="mt-4 text-ink-2 text-[16px] leading-relaxed max-w-2xl">
              Schools determine why and how their records are processed and remain responsible for
              their privacy programme. <BrandWordmarkText /> provides supporting controls: an
              append-only consent ledger, classification-driven retention, an internal breach timer,
              reviewed erasure, and configured identifier safeguards around AI-assisted work.
            </p>
            <p className="mt-3 max-w-2xl rounded-xl border border-line bg-surface px-4 py-3 text-[13.5px] leading-relaxed text-ink-2">
              <strong className="text-ink">Commencement matters.</strong> The DPDP Act and 2025 Rules
              are being brought into force in stages. As of August 2026, several substantive
              requirements have a later commencement date. This page describes product readiness,
              not a certification or a substitute for legal advice.{" "}
              <span className="whitespace-nowrap">
                <a
                  href="https://www.meity.gov.in/static/uploads/2024/02/Digital-Personal-Data-Protection-Act-2023.pdf"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Read the official DPDP Act PDF (opens in a new tab)"
                  className="font-semibold text-accent-active underline decoration-current/30 underline-offset-2 hover:decoration-current focus:outline-none focus-visible:ring-2 ring-primary rounded"
                >
                  Read the Act
                </a>
                {" · "}
                <a
                  href="https://www.meity.gov.in/static/uploads/2025/11/53450e6e5dc0bfa85ebd78686cadad39.pdf"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Read the official final DPDP Rules PDF (opens in a new tab)"
                  className="font-semibold text-accent-active underline decoration-current/30 underline-offset-2 hover:decoration-current focus:outline-none focus-visible:ring-2 ring-primary rounded"
                >
                  Read the final Rules
                </a>
              </span>
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Button size="lg" onClick={() => { track("trial_cta_click", { placement: "dpdp_page" }); navigate("/register"); }} data-testid="dpdp-register">
                {CTA_PRIMARY_LABEL} <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </Button>
              <Button size="lg" variant="outline" onClick={() => { track("demo_request", { placement: "dpdp_page" }); setSalesOpen(true); }} data-testid="dpdp-demo">
                Talk to us about DPDP
              </Button>
            </div>
            <ul className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-sm text-ink-2">
              {[
                "Append-only consent ledger",
                "Internal breach timer and evidence trail",
                "Deployment region documented before go-live",
              ].map((t) => (
                <li key={t} className="inline-flex items-center gap-1.5">
                  <Check className="w-4 h-4 text-positive shrink-0" aria-hidden="true" /> {t}
                </li>
              ))}
            </ul>
          </div>
          <div className="hidden lg:block">
            <ConsentLedgerPreview reduce={reduce} />
          </div>
          {/* PGS-11 — the mobile visitor is the primary visitor: the consent-ledger proof renders
              below the hero copy on <lg too, statically (reduce), instead of disappearing. */}
          <div className="lg:hidden">
            <ConsentLedgerPreview reduce testId="dpdp-ledger-preview-mobile" />
          </div>
        </section>

        {/* Obligation → shipped mechanism, one card each. */}
        <section className="border-y border-line bg-surface" aria-labelledby="dpdp-obligations-h">
          <div className="mx-auto max-w-6xl px-5 py-12 sm:py-16">
            <m.div {...reveal()} className="max-w-2xl">
              <h2 id="dpdp-obligations-h" className="font-heading font-bold text-2xl sm:text-[28px] text-ink tracking-tight">
                Five privacy duties. A supporting control for each.
              </h2>
              <p className="mt-3 text-ink-2 text-[15px] leading-relaxed">
                A practical view of the programme a school should prepare and the product controls
                available to support its authorised team.
              </p>
            </m.div>
            <div className="mt-8 space-y-5">
              {OBLIGATIONS.map((o, i) => (
                <m.article key={o.id} {...reveal(Math.min(i * 0.05, 0.2))}
                  className="rounded-2xl border border-line bg-canvas p-6 sm:p-7 lg:grid lg:grid-cols-[minmax(0,340px)_minmax(0,1fr)] lg:gap-10"
                  aria-labelledby={`dpdp-${o.id}-h`}>
                  <div>
                    <div className="flex items-start gap-3.5">
                      <span className={`mt-0.5 w-10 h-10 rounded-xl grid place-items-center shrink-0 ${o.accent}`}>
                        <o.icon className="w-5 h-5" aria-hidden="true" />
                      </span>
                      <div>
                        <p className="text-[11px] font-bold uppercase tracking-wide text-ink-subtle">Privacy duty to prepare for</p>
                        <h3 id={`dpdp-${o.id}-h`} className="mt-0.5 font-heading font-semibold text-ink text-[17px] leading-snug">{o.obligation}</h3>
                      </div>
                    </div>
                    <p className="mt-3 text-[14px] text-ink-2 leading-relaxed lg:pl-[3.375rem] lg:mt-2">{o.act}</p>
                  </div>
                  <div className="mt-5 lg:mt-0 lg:border-l lg:border-line lg:pl-10">
                    <p className="text-[11px] font-bold uppercase tracking-wide text-ink-subtle">What ships in the product</p>
                    <p className="mt-0.5 font-heading font-semibold text-ink text-[15px]">{o.mechanism}</p>
                    <ul className="mt-3 space-y-2.5">
                      {o.points.map(([lead, rest]) => (
                        <li key={lead} className="flex gap-2.5 text-[14px] leading-relaxed">
                          <CheckCircle2 className={`w-4 h-4 mt-1 shrink-0 ${o.accent.split(" ")[0]}`} aria-hidden="true" />
                          <span className="text-ink-2"><span className="font-semibold text-ink">{lead}</span> {rest}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </m.article>
              ))}
            </div>
          </div>
        </section>

        {/* Who does what — the fiduciary/processor framing, and the honest non-advice note. */}
        <section className="mx-auto max-w-6xl px-5 py-12 sm:py-16" aria-labelledby="dpdp-roles-h">
          <m.div {...reveal()} className="rounded-2xl border border-primary/20 bg-primary/[0.05] p-6 sm:p-7 max-w-3xl">
            <h2 id="dpdp-roles-h" className="font-heading font-semibold text-ink text-[17px] leading-snug">
              Your school remains responsible. We provide supporting controls.
            </h2>
            <p className="mt-2.5 text-[14.5px] text-ink-2 leading-relaxed">
              <BrandWordmarkText /> processes data on your school's instructions. The consent ledger, the
              retention windows, the breach timer, and the erasure path help authorised staff run a
              reviewable privacy programme. They do not decide lawful purpose, consent validity,
              notice content, retention law, or breach notification for the school. This page is not
              legal advice, and your school should take its own counsel on compliance.
            </p>
            <p className="mt-3 text-sm text-ink-2">
              The full security story — tenant isolation, AI privacy, access control, and how money
              moves — is on the{" "}
              {/* `text-accent` is the nav/large-accent token (tailwind.config: "not small body text").
                  At 14px on this `bg-primary/[0.05]` card it fell under 4.5:1 and axe flagged it
                  serious. `accent-active` is the AA-safe, theme-aware sibling (the same pairing the
                  fees section pills use), so this stays correct in dark mode too. The identical link
                  on /switch is NOT changed — it sits on an untinted surface and already passes. */}
              <Link to="/security" className="font-medium text-accent-active hover:underline rounded focus:outline-none focus-visible:ring-2 ring-primary">Security &amp; Compliance page</Link>.
            </p>
          </m.div>
        </section>

        {/* Close — one ask, stated as the outcome. */}
        <section className="mx-auto max-w-6xl px-5 pb-14 sm:pb-16">
          <div className="rounded-3xl bg-gradient-to-br from-[var(--vp-close-band-from)] via-[var(--vp-close-band-via)] to-[var(--vp-close-band-to)] p-8 sm:p-12 text-center relative overflow-hidden">
            <div className="absolute -top-20 -right-16 w-72 h-72 rounded-full bg-orange/15 blur-3xl pointer-events-none" aria-hidden="true" />
            <h2 className="font-heading font-bold text-[26px] sm:text-[32px] text-white tracking-tight max-w-2xl mx-auto leading-tight">
              Review the controls against your school's own privacy programme.
            </h2>
            <p className="text-white/75 mt-3 max-w-xl mx-auto leading-relaxed">
              Register free and see the consent ledger, retention windows, and breach workflow on
              your own data — or read the full security story first.
            </p>
            <div className="mt-7 flex flex-wrap gap-3 justify-center">
              <Button size="lg" className="bg-white text-primary hover:bg-white/90 shadow-lg" onClick={() => { track("trial_cta_click", { placement: "dpdp_page_close" }); navigate("/register"); }}>
                {CTA_PRIMARY_LABEL} <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </Button>
              <Button size="lg" variant="ghost" className="text-white border border-white/40 hover:bg-white/10" onClick={() => navigate("/security")}>
                Read the security story
              </Button>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <ContactSalesModal open={salesOpen} onClose={() => setSalesOpen(false)} contextLabel="DPDP readiness, data rights and school rollout controls" placement="dpdp_page" />
    </div>
    </MarketingMotion>
  );
}
